/**
 * The live A4 preview.
 *
 * Draws one sheet per printed page from the geometry and rendered view models the
 * server returns. Nothing here re-implements a question type: `render_preview()`
 * has already turned a payload into blanks, pairs and lettered choices, which is
 * the whole reason question types are plugins.
 *
 * Two properties are load-bearing and easy to lose:
 *
 * 1. **Sheets grow, they never clip.** The old Streamlit preview used a fixed
 *    aspect ratio plus `overflow: hidden`, so a two-page paper was silently cut
 *    down to one and nothing told the teacher the rest existed. If the estimate is
 *    going to be wrong it must be wrong visibly.
 * 2. **It is drawn to scale.** Sizes are in container-query units against the
 *    printable width, so 11.5pt body text really does measure about 4.05mm on
 *    screen. Fixed pixel sizes make an A4-shaped box whose fullness means nothing.
 */

import { useMemo } from 'react'
import type { CSSProperties } from 'react'
import type { Preview as PreviewData } from '../../lib/api/client'
import { Picture } from '../../components/Picture'
import { subitemLabel } from '../../lib/labels'
import './preview.css'

const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297
const PT_TO_MM = 25.4 / 72

export function Preview({
  data,
  loading,
  error,
}: {
  data?: PreviewData
  loading?: boolean
  error?: boolean
}) {
  const style = useMemo(() => (data ? sheetVariables(data) : undefined), [data])

  if (!data) {
    return (
      <div className="tc-preview">
        {/* A blank dashed box was the worst of the three states to land on: a
            failed preview looked exactly like an empty one, so nothing told the
            teacher whether the paper was fine and the render had broken. */}
        <div className="tc-sheet-placeholder">
          {loading
            ? 'Building the preview…'
            : error
              ? 'The preview could not be built. Your paper is safe — reload to try again.'
              : 'Nothing to preview yet.'}
        </div>
      </div>
    )
  }

  return (
    <div className="tc-preview" style={style}>
      {data.pages.map((page) => (
        <div key={page.number}>
          <div className={`tc-sheet tc-page-border-${data.header.page_border}`}>
            {data.header.page_border !== 'none' && (
              <div className="tc-paper-border" aria-hidden="true" />
            )}
            <div className="tc-page-body">
              {data.header.paper_layout === 'exam_grid' ? (
                <>
                  {page.number > 1 && <ExamContinuationHeader data={data} page={page.number} />}
                  {page.items.map((item, index) =>
                    item.kind === 'header' ? <Header key={index} data={data} /> : null,
                  )}
                  <ExamGridPage data={data} pageNumber={page.number} />
                </>
              ) : (
                page.items.map((item, index) => {
                if (item.kind === 'header') return <Header key={index} data={data} />
                if (item.kind === 'section')
                  return (
                    <div key={index}>
                      {item.section_title && (
                        <div className="tc-section-title">{item.section_title}</div>
                      )}
                      {item.section_instructions && (
                        <div className="tc-section-instr">{item.section_instructions}</div>
                      )}
                    </div>
                  )
                return item.question ? <Question key={index} view={item.question} /> : null
                })
              )}
            </div>
            {(data.header.footer_text ||
              (data.header.show_page_numbers && data.header.paper_layout !== 'exam_grid')) && (
              <div className="tc-page-footer">
                <span>{data.header.footer_text}</span>
                {data.header.show_page_numbers && (
                  <span className="tc-page-number">{page.number}</span>
                )}
              </div>
            )}
          </div>
          <div className="tc-sheet-label">
            Page {page.number} of {data.page_count}
          </div>
        </div>
      ))}

      {/* "About" is doing real work. Word paginates with the installed font's own
          metrics, so a confident number the printed file contradicts would be
          exactly the failure this product exists to remove. */}
      <div className="tc-preview-caption">
        A4 · about {data.page_count} page{data.page_count === 1 ? '' : 's'} when printed
      </div>
    </div>
  )
}

function Header({ data }: { data: PreviewData }) {
  const header = data.header
  const layout = headerLayout(header.layout)
  return (
    <>
      <div className="tc-head">
        {(header.logo_image_id || header.title || header.fixed_lines.length > 0) && (
          <div
            className={`tc-head-identity tc-head-layout-${layout} ${
              header.logo_image_id ? '' : 'has-no-logo'
            }`}
            style={{
              '--tc-head-logo-width': `${Math.max(30, Math.min(50, header.logo_width_percent))}%`,
            } as CSSProperties}
          >
            {header.logo_image_id && (
              <Picture className="tc-head-logo" id={header.logo_image_id} />
            )}
            <div className="tc-head-copy" style={{ textAlign: header.title_alignment }}>
              {header.paper_layout === 'exam_grid' && header.fixed_lines.map((line, index) => (
                <div key={index} className="tc-exam-school-line">
                  {line}
                </div>
              ))}
              {header.title && <div className="tc-paper-title">{header.title}</div>}
              {header.paper_layout !== 'exam_grid' && header.fixed_lines.map((line, index) => (
                <div key={index} className="tc-fixed">
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}
        {header.fields.length > 0 && (
          <div
            className={`tc-fields tc-blank-lines-${header.blank_line_style} ${
              header.details_border ? 'has-border' : ''
            }`}
            style={{
              gridTemplateColumns: `repeat(${data.header.fields_per_row}, minmax(0, 1fr))`,
            }}
          >
            {header.fields.map((field, index) => {
              const columns = data.header.fields_per_row
              const nameSpan = Math.min(2, Math.max(1, columns - 1))
              return (
                <div
                  key={index}
                  className={`tc-field-line tc-field-${field.key}`}
                  style={{
                    gridColumn: field.key === 'student_name' ? 'span ' + nameSpan : undefined,
                  }}
                >
                  <span className="tc-field-label">{field.label}:</span>
                  {field.value ? (
                    <strong>{field.value}</strong>
                  ) : (
                    <span className="tc-blank" aria-hidden="true">&nbsp;</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {!header.details_border && header.paper_layout !== 'exam_grid' && <div className="tc-hr" />}
    </>
  )
}

function ExamContinuationHeader({ data, page }: { data: PreviewData; page: number }) {
  return (
    <div className="tc-exam-continuation">
      <span>{data.header.logo_image_id && <Picture id={data.header.logo_image_id} alt="" />}</span>
      {data.header.show_page_numbers && <span>Page {page} of {data.page_count}</span>}
    </div>
  )
}

function ExamGridPage({ data, pageNumber }: { data: PreviewData; pageNumber: number }) {
  const page = data.pages.find((candidate) => candidate.number === pageNumber)
  if (!page) return null
  const questions = page.items.filter((item) => item.question)
  if (questions.length === 0) return null
  let sectionTitle = ''
  for (const candidate of data.pages) {
    if (candidate.number > pageNumber) break
    for (const item of candidate.items) {
      if (item.kind === 'section' && item.section_title) sectionTitle = item.section_title
    }
  }
  return (
    <div className="tc-exam-grid">
      <div className="tc-exam-grid-row tc-exam-grid-heading">
        <div>Q.No</div><div>{sectionTitle}</div><div>Marks</div>
      </div>
      {questions.map((item, index) => {
        const view = item.question!
        const continuation = Boolean(view.extra?.pagination_fragment)
        const label = continuation
          ? ''
          : String(view.number_label || view.number).replace(/\.$/, '')
        return (
          <div className="tc-exam-grid-row" key={`${pageNumber}-${index}-${view.id}`}>
            <div className="tc-exam-number">{label ? (label.toUpperCase().startsWith('Q') ? label : `Q${label}.`) : ''}</div>
            <div className="tc-exam-content"><Question view={view} embedded /></div>
            <div className="tc-exam-mark">{continuation || view.marks == null ? '' : `${view.marks}m`}</div>
          </div>
        )
      })}
    </div>
  )
}

type HeaderLayout = 'centered' | 'logo_left' | 'logo_right' | 'banner'

function headerLayout(value: string): HeaderLayout {
  return value === 'logo_left' || value === 'logo_right' || value === 'banner'
    ? value
    : 'centered'
}

function Question({
  view,
  embedded = false,
}: {
  view: NonNullable<PreviewData['pages'][number]['items'][number]['question']>
  embedded?: boolean
}) {
  const hasImages = view.options.some((option) => option.image_id)
  const classes = [
    'tc-q',
    embedded ? 'tc-q-embedded' : '',
    view.passage_id ? 'tc-q-comprehension' : '',
    view.extra?._ends_question ? 'tc-q-end' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes}>
      {!(view.extra?.pagination_fragment && !view.stem) && (
        <div className="tc-q-head">
          {!embedded && view.numbered && <span className="tc-q-num">{view.number_label || view.number}.</span>}
          <span className={view.numbered ? 'tc-q-stem' : 'tc-passage-title'}>
            {view.stem || (view.numbered ? <em>Untitled question</em> : null)}
          </span>
          {!embedded && view.marks != null && <span className="tc-marks">[{formatMarks(view.marks)}]</span>}
        </div>
      )}

      {Array.isArray(view.extra?.paragraphs) && (
        <div className="tc-passage">
          {(view.extra.paragraphs as string[]).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      )}

      {view.instructions && <div className="tc-q-instr">{view.instructions}</div>}

      {view.image_ids.map((id) => (
        <Picture key={id} className="tc-q-img" id={id} />
      ))}

      {view.options.length > 0 && (
        <div
          className={
            view.option_inline && !hasImages
              ? 'tc-opt-inline'
              : view.option_columns > 1 || hasImages
                ? 'tc-opt-grid'
                : 'tc-opt-list'
          }
          style={
            view.option_columns > 1 || hasImages
              ? { gridTemplateColumns: `repeat(${Math.max(2, view.option_columns)}, 1fr)` }
              : undefined
          }
        >
          {view.options.map((option, index) => (
            <div
              key={index}
              className={option.is_correct ? 'tc-opt tc-opt-correct' : 'tc-opt'}
              style={view.image_align === 'center' ? { textAlign: 'center' } : undefined}
            >
              {/* Off for colouring, optional for odd-one-out - the screen has to
                  agree with the page. */}
              {view.show_option_labels && <span className="tc-opt-label">({option.label})</span>}{' '}
              {option.image_id && (
                <Picture
                  className="tc-opt-thumb"
                  id={option.image_id}
                  style={view.image_align === 'center' ? { margin: '0 auto' } : undefined}
                />
              )}
              {option.text}
              {option.is_correct && <span className="tc-tick"> ✓</span>}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(view.extra?.left) && <Matching extra={view.extra} />}

      {view.extra?.response_layout === 'alphabet_handwriting' && (
        <AlphabetHandwriting extra={view.extra} />
      )}

      {view.extra?.response_layout === 'number_sequence_grid' && (
        <NumberSequenceGrid extra={view.extra} />
      )}

      {Array.isArray(view.blocks) && <ActivityBlocks blocks={view.blocks} />}

      {view.answer_lines > 0 && (
        <div className="tc-answer-lines">
          {Array.from({ length: view.answer_lines }, (_, index) => (
            <div key={index} className="tc-line" />
          ))}
        </div>
      )}
    </div>
  )
}

function AlphabetHandwriting({ extra }: { extra: Record<string, unknown> }) {
  const rows = Math.max(0, Number(extra.practice_rows) || 0)

  return (
    <div className="tc-handwriting">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="tc-handwriting-band">
          <span className="tc-guide-line tc-guide-outer" />
          <span className="tc-guide-line tc-guide-inner" />
          <span className="tc-guide-line tc-guide-inner" />
          <span className="tc-guide-line tc-guide-outer" />
        </div>
      ))}
    </div>
  )
}

function NumberSequenceGrid({ extra }: { extra: Record<string, unknown> }) {
  const cells = Math.max(0, Number(extra.grid_cells) || 0)
  const columns = Math.max(1, Number(extra.grid_columns) || 1)
  const requestedSize = String(extra.grid_box_size || 'medium')
  const size = ['small', 'medium', 'large'].includes(requestedSize) ? requestedSize : 'medium'
  const requestedWidth = String(extra.grid_cell_width || 'full_page')
  const width = ['narrow', 'standard', 'wide', 'full_page'].includes(requestedWidth)
    ? requestedWidth
    : 'full_page'
  const columnWidth =
    width === 'full_page' ? '1fr' : `var(--page-number-width-${width})`

  return (
    <div
      className={`tc-number-grid tc-number-grid-${size}`}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, ${columnWidth}))` }}
    >
      {Array.from({ length: cells }, (_, index) => (
        <span key={index} className="tc-number-cell" />
      ))}
    </div>
  )
}

type ActivityBlock = Record<string, unknown>

function ActivityBlocks({ blocks }: { blocks: ActivityBlock[] }) {
  return (
    <>
      {blocks.map((block, blockIndex) => {
        if (block.kind === 'missing_numbers') {
          const rows = (block.rows as Record<string, unknown>[]) ?? []
          const columns = Math.max(1, Number(block.columns) || 1)
          return (
            <div
              className="tc-missing-grid"
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              key={blockIndex}
            >
              {rows.map((row, index) => (
                <div className="tc-missing-row" key={index}>
                  <strong>{String(row.label)})</strong>
                  {((row.cells as Array<number | null>) ?? []).map((cell, cellIndex) => (
                    <span key={cellIndex} className={cell == null ? 'tc-missing-blank' : ''}>
                      {cell == null ? '' : cell}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )
        }

        if (block.kind === 'picture_count') {
          const groups = (block.groups as Record<string, unknown>[]) ?? []
          const picturesPerRow = Math.max(
            4,
            Math.min(12, Number(block.pictures_per_row) || 9),
          )
          return (
            <div className="tc-count-groups" key={blockIndex}>
              {groups.map((group, index) => (
                <div className="tc-count-row" key={index}>
                  <strong>{String(group.label)})</strong>
                  <div
                    className="tc-count-pictures"
                    style={{
                      gridTemplateColumns: `repeat(${picturesPerRow}, minmax(0, 1fr))`,
                    }}
                  >
                    {Array.from({ length: Math.max(0, Number(group.repeat_count) || 0) }, (_, i) => (
                      <Picture
                        className="tc-count-picture"
                        id={String(group.image_id)}
                        key={i}
                      />
                    ))}
                  </div>
                  <span className="tc-response-box" />
                </div>
              ))}
            </div>
          )
        }

        if (block.kind === 'size_comparison') {
          const groups = (block.groups as Record<string, unknown>[]) ?? []
          return (
            <div className="tc-size-groups" key={blockIndex}>
              {groups.map((group, index) => {
                const scales = (group.scales as number[]) ?? [0.65, 0.85, 1.05]
                const actions = (group.actions as string[]) ?? ['tick', 'circle', 'cross']
                return (
                  <div className="tc-size-row" key={index}>
                    <strong>{String(group.label)})</strong>
                    {scales.map((scale, position) => (
                      <div className="tc-size-item" key={position}>
                        <Picture
                          id={String(group.image_id)}
                          style={{ width: `calc(var(--page-thumb) * ${scale})` }}
                        />
                        <span title={actions[position]}>□</span>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          )
        }

        if (block.kind === 'circle_correct') {
          const items = (block.items as Record<string, unknown>[]) ?? []
          const columns = Math.max(1, Number(block.columns) || 1)
          const requestedSize = String(block.picture_size ?? 'medium')
          const pictureSize = ['small', 'medium', 'large'].includes(requestedSize)
            ? requestedSize
            : 'medium'
          return (
            <div
              className={`tc-circle-grid tc-circle-size-${pictureSize}`}
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              key={blockIndex}
            >
              {items.map((item, index) => {
                const choices = (item.choices as string[]) ?? []
                const answerIndex =
                  typeof item.answer_index === 'number' ? item.answer_index : null
                return (
                  <div className="tc-circle-item" key={index}>
                    <strong>{String(item.label)})</strong>
                    {item.image_id ? (
                      <div className="tc-circle-picture">
                        <Picture id={String(item.image_id)} alt="" />
                      </div>
                    ) : null}
                    {item.text ? (
                      <div
                        className={`tc-circle-prompt ${
                          ['△', '▭', '○', '□', '◯'].includes(String(item.text).trim())
                            ? 'is-shape'
                            : ''
                        }`}
                      >{String(item.text)}</div>
                    ) : null}
                    <div
                      className="tc-circle-choices"
                      style={{
                        gridTemplateColumns: `repeat(${Math.max(
                          1,
                          choices.length,
                        )}, minmax(0, 1fr))`,
                      }}
                    >
                      {choices.map((choice, choiceIndex) => (
                        <span
                          className={answerIndex === choiceIndex ? 'tc-opt-correct' : ''}
                          key={choiceIndex}
                        >
                          {choice}{answerIndex === choiceIndex ? ' ✓' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        }
        if (block.kind === 'write_in_box') {
          const items = (block.items as Record<string, unknown>[]) ?? []
          const columns = Math.max(1, Number(block.columns) || 1)
          const requestedSize = String(block.picture_size ?? 'medium')
          const pictureSize = ['small', 'medium', 'large'].includes(requestedSize)
            ? requestedSize
            : 'medium'
          const boxSize = Math.max(12, Math.min(30, Number(block.box_size_mm) || 18))
          return (
            <div
              className={`tc-write-grid tc-write-size-${pictureSize}`}
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              key={blockIndex}
            >
              {items.map((item, index) => {
                const wordCompletion = block.mode === 'complete_word'
                return (
                  <div
                    className={`tc-write-item${wordCompletion ? ' is-word-completion' : ''}`}
                    key={index}
                  >
                    <strong>{String(item.label)})</strong>
                    <div className="tc-write-prompt">
                      {item.image_id ? <Picture id={String(item.image_id)} alt="" /> : null}
                      {!wordCompletion && item.text ? (
                        <span className="tc-write-text">{String(item.text)}</span>
                      ) : null}
                    </div>
                    {wordCompletion ? (
                      <CompletionPattern
                        pattern={String(item.text ?? '')}
                        answer={String(item.answer ?? '')}
                      />
                    ) : (
                      <span
                        className="tc-write-box"
                        style={{
                          width: `calc(var(--page-mm) * ${boxSize})`,
                          height: `calc(var(--page-mm) * ${boxSize})`,
                        }}
                      >
                        {item.answer ? (
                          <strong className="tc-box-answer">{String(item.answer)}</strong>
                        ) : null}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )
        }

        if (block.kind === 'tick_cross_pictures') {
          const items = (block.items as Record<string, unknown>[]) ?? []
          const columns = Math.max(1, Number(block.columns) || 1)
          const requestedSize = String(block.picture_size ?? 'medium')
          const pictureSize = ['small', 'medium', 'large'].includes(requestedSize)
            ? requestedSize
            : 'medium'
          return (
            <div
              className={`tc-tick-grid tc-tick-size-${pictureSize}`}
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              key={blockIndex}
            >
              {items.map((item, index) => {
                const mark = item.mark === 'tick' ? '✓' : item.mark === 'cross' ? '✗' : ''
                return (
                  <div className="tc-tick-item" key={index}>
                    <strong>{String(item.label)})</strong>
                    <Picture id={String(item.image_id)} alt="" />
                    {item.text ? <div className="tc-tick-caption">{String(item.text)}</div> : null}
                    <span className="tc-tick-box">{mark}</span>
                  </div>
                )
              })}
            </div>
          )
        }
        if (block.kind === 'drawing_area') {
          return (
            <div
              className={block.border ? 'tc-drawing-area is-bordered' : 'tc-drawing-area'}
              style={{ height: `calc(var(--page-number-small) * ${Number(block.height_mm) / 12})` }}
              key={blockIndex}
            />
          )
        }

        return null
      })}
    </>
  )
}

/** Matching draws two columns with a deliberate empty gutter - without somewhere
 *  to draw the joining lines the exercise does not work on paper. */
type MatchSide = { text?: string; image?: { id?: string } | null }

function Matching({ extra }: { extra: Record<string, unknown> }) {
  const left = (extra.left as MatchSide[]) ?? []
  const right = (extra.right as MatchSide[]) ?? []
  const rows = Math.max(left.length, right.length)

  const half = (side: MatchSide | undefined, marker: string) =>
    side ? (
      <div>
        {marker} {side.text}
        {side.image?.id && <Picture className="tc-match-img" id={side.image.id} />}
      </div>
    ) : (
      <div />
    )

  return (
    <div className="tc-match">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="tc-match-row">
          {half(left[index], `${subitemLabel(index)}.`)}
          {/* The gutter is deliberately empty: it is where the child draws. */}
          <div />
          {half(right[index], `${String.fromCharCode(65 + index)}.`)}
        </div>
      ))}
    </div>
  )
}

function CompletionPattern({ pattern, answer }: { pattern: string; answer: string }) {
  const start = pattern.indexOf('_')
  if (start < 0) return <span className="tc-word-pattern">{pattern}</span>

  let end = start
  while (end < pattern.length && pattern[end] === '_') end += 1
  const blank = answer || ' '.repeat(Math.max(2, (end - start) * 2))
  return (
    <span className="tc-word-pattern">
      {pattern.slice(0, start)}
      <span className={`tc-word-blank${answer ? ' is-answer' : ''}`}>{blank}</span>
      {pattern.slice(end)}
    </span>
  )
}

/**
 * The sheet's CSS variables, derived from the school's own margins.
 *
 * `cqw` is one percent of the query container's *content* box, so the base is the
 * printable width rather than the 210mm sheet. Using the sheet width here draws
 * everything about 20% too large - a mistake worth naming, because the number
 * looks plausible either way.
 */
function sheetVariables(data: PreviewData): React.CSSProperties {
  const h = data.header
  const printableWidth = A4_WIDTH_MM - h.margin_left_mm - h.margin_right_mm
  const cqw = (mm: number) => `${((mm / printableWidth) * 100).toFixed(4)}cqw`
  const pt = (points: number) => cqw(points * PT_TO_MM)
  const bodyPoints = h.font_size_pt ?? 12
  const fontName = h.font_name || 'Palatino Linotype'
  const titlePoints =
    h.title_size === 'small' ? 12 : h.title_size === 'large' ? 20 : 16
  const questionGapPoints =
    data.question_spacing === 'one_enter'
      ? bodyPoints * 0.5
      : data.question_spacing === 'blank_line'
        ? bodyPoints * 1.22 * 1.15
        : 0

  return {
    // Padding is a percentage of the sheet's own width, which resolves against the
    // parent - not against the content box like cqw does.
    '--sheet-pad-top': `${((h.margin_top_mm / A4_WIDTH_MM) * 100).toFixed(4)}%`,
    '--sheet-pad-bottom': `${((h.margin_bottom_mm / A4_WIDTH_MM) * 100).toFixed(4)}%`,
    '--sheet-pad-left': `${((h.margin_left_mm / A4_WIDTH_MM) * 100).toFixed(4)}%`,
    '--sheet-pad-right': `${((h.margin_right_mm / A4_WIDTH_MM) * 100).toFixed(4)}%`,
    '--page-footer-bottom': `${((h.margin_bottom_mm / A4_WIDTH_MM) * 50).toFixed(4)}%`,
    // The ::before reserves the *printable* height as a minimum. Its percentage
    // resolves against the content box, so this is the printable area's aspect
    // ratio, not the sheet's - getting that wrong draws every page short by
    // exactly the top and bottom margins.
    '--sheet-reserve': `${(((A4_HEIGHT_MM - h.margin_top_mm - h.margin_bottom_mm) / printableWidth) * 100).toFixed(3)}%`,
    '--page-font-family': `"${fontName}", Palatino, serif`,
    '--page-font': pt(bodyPoints),
    '--page-line': '1.403',
    '--page-heading': pt(bodyPoints + 2.5),
    '--page-title': pt(titlePoints),
    '--page-marks': pt(bodyPoints - 1),
    '--page-indent': cqw(14),
    '--page-mm': cqw(1),
    '--page-gap-q': pt(9),
    '--page-gap-sm': pt(3),
    '--page-gap-md': pt(4),
    '--page-question-gap': pt(questionGapPoints),
    '--page-rule': cqw(bodyPoints * PT_TO_MM * 1.403),
    '--page-img': cqw(60),
    '--page-thumb': cqw(28),
    '--page-guide-zone': cqw(5),
    '--page-number-small': cqw(12),
    '--page-number-medium': cqw(16),
    '--page-number-large': cqw(20),
    '--page-number-width-narrow': cqw(24),
    '--page-number-width-standard': cqw(36),
    '--page-number-width-wide': cqw(48),
    '--page-exam-number': cqw(18),
    '--page-exam-marks': cqw(19),
  } as React.CSSProperties
}

function formatMarks(marks: number) {
  const whole = Number.isInteger(marks) ? String(marks) : String(marks)
  return `${whole} mark${marks === 1 ? '' : 's'}`
}
